import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { StyleSheet } from 'react-native'

import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import { B2, H4, SH3, Text } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import { zetApi } from '@zet/network'
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field'

import { useIsApplicationV2 } from '../../api/applicationDetails/hook'
import { useResendOtp } from '../../api/resendOtp/hooks/useResendOtp'
import { brandsI18n } from '../../localization'

const { locale = 'en' } = zetApi.defaults.headers.common

const DEFAULT_OTP_LENGTH = 6
interface Props {
  onChange: (otp: string) => void
  isError?: boolean
  description: string
  hash: string
  otpLength?: number
  title?: string
  resendCount?: number
}

export interface OtpVerificationRef {
  setTimer: (arg: number) => void
}

const OtpVerification = forwardRef<OtpVerificationRef, Props>(
  (
    {
      onChange = () => null,
      isError = false,
      description,
      hash,
      title,
      otpLength = DEFAULT_OTP_LENGTH,
      resendCount,
    },
    ref,
  ) => {
    const { mutate } = useResendOtp()

    const [value, setValue] = useState<string>('')
    const [timer, setTimer] = useState<number>(1)
    const [error, setError] = useState<string>('')

    const [props, getCellOnLayoutHandler] = useClearByFocusCell({
      value,
      setValue,
    })

    const isV2 = useIsApplicationV2(hash)

    useImperativeHandle(ref, () => ({
      setTimer: (time: number) => {
        setTimer(time)
      },
    }))

    useEffect(() => {
      if (isError && !isV2) {
        setError(brandsI18n.t('common.incorrectOtp'))
      }
    }, [isError])

    const interval = 1000
    const timerRef = useRef<ReturnType<typeof setInterval>>()
    let prev = 0
    let diff = 0
    const timerCallback = () => {
      const date = new Date()
      setTimer((i) => {
        const current = date.getSeconds()
        if (diff === 0) {
          diff = 1
          prev = date.getSeconds()
          return 1
        }

        if (prev > current) diff = 60 - prev + current
        else diff = current - prev

        prev = date.getSeconds()

        if (i > 1)
          if (i + diff < 1) {
            clearInterval(timerRef?.current)
            return 1
          } else {
            return i - diff
          }
        else {
          clearInterval(timerRef?.current)
          return 0
        }
      })
    }
    const timerFunction = () => {
      timerRef.current = setInterval(timerCallback, interval)
    }

    const otpTimer = useCallback(timerFunction, [timerRef])

    useEffect(() => {
      otpTimer()
      return () => {
        clearInterval(timerRef?.current)
      }
    }, [])

    const fieldRef = useBlurOnFulfill({ value, cellCount: otpLength })

    return (
      <Stack>
        <Stack className="mx-3 mt-3 justify-center">
          <H4 className="">{title}</H4>
          <SH3 className="text-grays-60 mt-1 text-left">{description}</SH3>
        </Stack>
        <CodeField
          onBlur={() => null}
          ref={fieldRef}
          value={value}
          {...props}
          onChangeText={(e) => {
            setError('')
            setValue(e)
            onChange(e)
          }}
          rootStyle={styles.codeFieldRoot}
          caretHidden={true}
          cellCount={otpLength}
          keyboardType="number-pad"
          renderCell={({ index, symbol, isFocused }) => (
            <Text
              key={index}
              onLayout={getCellOnLayoutHandler(index)}
              style={[styles.cell, isFocused && styles.focusCell]}
            >
              {symbol || (isFocused ? <Cursor /> : null)}
            </Text>
          )}
        />

        {!!error && (
          <XStack className="bg-error-20 m-3 mb-0 rounded-lg p-2">
            <Icon type="ErrorOutlined" size={22} color={zetColors.error[100]} />
            <Text className="ml-2">{error}</Text>
          </XStack>
        )}

        <>
          {timer === 0 ? (
            <Stack className="my-2 items-center">
              <Stack className="group">
                <Button
                  eventName="click_action"
                  eventPayload={{ item_name: 'login_signup_resend_otp' }}
                  textOnly={true}
                  intent="primary"
                  size={'medium'}
                  onPress={async () => {
                    prev = 0
                    diff = 0
                    mutate({ hash })
                    timerFunction()
                  }}
                >
                  <Text className="text-primary-100 group-hover:text-white">
                    {brandsI18n.t('axis.resendOtp')}
                  </Text>
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack
              className="items-center justify-center"
              style={{
                flexDirection: locale === 'hi' ? 'row-reverse' : 'row',
              }}
            >
              <B2 className="text-grays-60 my-3 text-center">
                {i18n.t('otpInput.resendOtpIn')}
              </B2>
              <SH3 className="text-primary-100">
                00:{timer < 10 ? `0${timer}` : timer}
              </SH3>
            </Stack>
          )}
        </>
      </Stack>
    )
  },
)

export default OtpVerification

const styles = StyleSheet.create({
  codeFieldRoot: {
    marginTop: 38,
    marginHorizontal: 16,
    justifyContent: 'space-around',
  },
  cell: {
    width: 48,
    height: 48,
    fontSize: 24,
    fontWeight: '500',
    borderWidth: 1,
    borderColor: zetColors.grays[40],
    textAlign: 'center',
    borderRadius: 6,
    backgroundColor: '#F9F9FB',
    textAlignVertical: 'center',
    paddingTop: 10,
    paddingBottom: 0,
  },
  focusCell: {
    borderColor: '#007AFF',
  },
  resendButton: {
    color: zetColors.primary[100],
  },
})
